import Axios, { AxiosHeaders } from "axios";
import Qs from "qs";
import { decamelizeKeys } from "humps";
import { getAccessToken } from "utilities/access-token";
import { isValid, parseISO } from "date-fns";
import { getClientId } from "./client-id";
import { captureException } from "@sentry/react";

export const BASE_URL = "/api";

// Dates formats the backend might send.
export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
export const AUTO_DATE_IGNORED_KEYS = ["tag"];

const languageIsoCodesTransformations: Record<string, string> = {
    zhcn: "zh-cn",
    zhtw: "zh-tw",
};

export const transformLanguageIsoCode = (languageIsoCode: string): string => {
    const lowerCaseIsoCode = languageIsoCode.toLowerCase();
    return languageIsoCodesTransformations[lowerCaseIsoCode] ?? lowerCaseIsoCode;
};

/**
 * JSON reviver that transforms API response values: date strings to Date objects,
 * interaction types to uppercase, and language ISO codes to standard format.
 * Used by transformResponse and can be reused in tests to apply the same transforms.
 */
export const responseReviver = (key: string, value: unknown): unknown => {
    try {
        // Attempt to transform strings to dates.
        if (typeof value === "string" && DATE_PATTERN.test(value) && !AUTO_DATE_IGNORED_KEYS.includes(key)) {
            try {
                const date = parseISO(value);
                return isValid(date)
                    ? parseISO(
                          value.replace(
                              /([+-])(\d{2}:\d{2})/,
                              (_, sign, offset) => `${sign === "+" ? "-" : "+"}${offset}`
                          )
                      )
                    : value;
            } catch {
                return value;
            }
        }

        if ((key === "interactionType" || key === "topInteractionType") && typeof value === "string") {
            return value.toUpperCase().replaceAll("_", " ");
        }

        if ((key === "interactionType" || key === "interactionTypes") && Array.isArray(value)) {
            return value.map((interaction) =>
                typeof interaction === "string" ? interaction.toUpperCase().replaceAll("_", " ") : interaction
            );
        }

        if (key === "language" && typeof value === "string") {
            return languageIsoCodesTransformations[value] ?? value;
        }
        if (key === "languageIsoCode" && typeof value === "string") {
            return transformLanguageIsoCode(value);
        }

        if ((key === "languageIsoCode" || key === "languageIsoCodes") && Array.isArray(value)) {
            return value.map((languageIsoCode) =>
                typeof languageIsoCode === "string" ? transformLanguageIsoCode(languageIsoCode) : languageIsoCode
            );
        }

        return value;
    } catch (error) {
        captureException(error);
        return value;
    }
};

const axios = Axios.create({
    baseURL: BASE_URL,
    paramsSerializer: (parameters) =>
        Qs.stringify(decamelizeKeys(parameters), {
            arrayFormat: "repeat",
            skipNulls: true,
        }),
    transformResponse: (data, headers) => {
        // Axios may pass data that is already parsed (depending on configuration); return it unchanged.
        if (typeof data !== "string") {
            return data;
        }

        const trimmedData = data.trim();

        // Empty or whitespace-only bodies (aborted requests, 204 No Content, proxy timeouts etc.) are not JSON.
        if (trimmedData.length === 0) {
            return null;
        }

        // Only parse responses that declare a JSON content type. When no content type is present at all, fall
        // back to a conservative sniff so JSON payloads are still parsed while anything else (CSV exports, HTML
        // error pages from the load balancer etc.) comes through as the raw string.
        //
        // The header is read through `AxiosHeaders` rather than a plain index because header names are not
        // guaranteed to be lower case here: axios only trims keys before calling `transformResponse`, so a
        // server (or a test mock) replying with `Content-Type` would make an index lookup silently miss.
        // `AxiosHeaders.get` matches case insensitively.
        const contentType = headers === undefined ? undefined : AxiosHeaders.from(headers).get("content-type");
        const isDeclaredJson =
            typeof contentType === "string" && contentType.toLowerCase().includes("application/json");
        // Only objects and arrays are sniffed. Bare JSON primitives (`true`, `null`, numbers, quoted strings)
        // pass through as raw strings, which is safe because every endpoint in the app returns an object or array.
        const looksLikeJson = trimmedData.startsWith("{") || trimmedData.startsWith("[");

        if (!isDeclaredJson && !(contentType == null && looksLikeJson)) {
            return data;
        }

        try {
            return JSON.parse(data, responseReviver);
        } catch {
            // If there was an error while parsing the JSON, return the raw string. A genuinely failed request
            // still surfaces through the normal axios error paths so there is no need to report it here.
            return data;
        }
    },
});

/**
 * Put the access token in a header.
 */
export const createAuthorisationHeader = () => `Bearer ${getAccessToken()}`;

axios.interceptors.request.use((request) => {
    const clientId = getClientId();

    if (clientId !== null) {
        request.headers.set("WatchTower-Client-ID", clientId);
    }
    request.headers.set("Authorization", createAuthorisationHeader());
    return request;
});

export { axios };

export default Axios;
